<template>
  <div>
    <b-row>
      <b-col cols="6">
        <b-form-group>
          <template #label>
            ราคาเต็ม <span class="text-danger">*</span>
          </template>
          <b-input-group append="บาท">
            <b-form-input
              :value="formatNumberInput(form.price)"
              @input="(val) => updateValue('price', val)"
              @keydown="onlyNumberKey"
              placeholder="0"
            ></b-form-input>
          </b-input-group>
        </b-form-group>
      </b-col>
      <b-col cols="6">
        <b-form-group label="ราคาลด (ราคาขายจริง)">
          <b-input-group prepend="ลดเหลือ" append="บาท">
            <b-form-input
              :value="formatNumberInput(form.promotionPrice)"
              @input="(val) => updateValue('promotionPrice', val)"
              @keydown="onlyNumberKey"
              :placeholder="form.price ? String(form.price) : '0'"
              :state="promotionState"
            ></b-form-input>
          </b-input-group>
          <b-form-invalid-feedback :state="promotionState">
            ราคาลดต้องน้อยกว่าราคาเต็ม และมากกว่า 0
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
      <b-col cols="6">
        <b-form-group>
          <template #label>
            วันที่เริ่มเรียน <span class="text-danger">*</span>
          </template>
          <b-input-group class="mb-3">
            <b-form-input
              id="start-learning-date"
              v-model="formattedStartLearningDate"
              type="text"
              placeholder="วว-ดด-ปปปป"
              autocomplete="off"
            ></b-form-input>
            <b-input-group-append>
              <b-form-datepicker
                v-model="startLearningDate"
                button-only
                right
                locale="th-TH"
                aria-controls="start-learning-date"
                :min="dateFormat"
                :max="finishLearningDate"
              ></b-form-datepicker>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </b-col>
      <b-col cols="6">
        <b-form-group>
          <template #label>
            วันที่จบคอร์ส <span class="text-danger">*</span>
          </template>
          <b-input-group class="mb-3">
            <b-form-input
              id="end-learning-date"
              v-model="formattedFinishLearningDate"
              type="text"
              placeholder="วว-ดด-ปปปป"
              autocomplete="off"
            ></b-form-input>
            <b-input-group-append>
              <b-form-datepicker
                v-model="finishLearningDate"
                button-only
                right
                locale="th-TH"
                aria-controls="end-learning-date"
                :min="startLearningDate"
              ></b-form-datepicker>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col cosl="3">
        <b-form-group>
          <template #label>
            เวลาเริ่มเรียน <span class="text-danger">*</span>
          </template>
          <b-input-group class="mb-3">
            <b-form-input
              id="start-learning-time"
              v-model="classStartTime"
              type="text"
              placeholder="HH:mm"
            ></b-form-input>
            <b-input-group-append>
              <b-form-timepicker
                v-model="classStartTime"
                button-only
                right
                locale="en"
                :hour12="false"
                aria-controls="start-learning-time"
              ></b-form-timepicker>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </b-col>
      <b-col cosl="3">
        <b-form-group>
          <template #label>
            เวลาที่สิ้นสุด <span class="text-danger">*</span>
          </template>
          <b-input-group class="mb-3">
            <b-form-input
              id="end-learning-time"
              v-model="classFinishTime"
              type="text"
              placeholder="HH:mm"
            ></b-form-input>
            <b-input-group-append>
              <b-form-timepicker
                v-model="classFinishTime"
                button-only
                right
                locale="en"
                :hour12="false"
                :state="isTimeInvalid ? false : null"
                aria-controls="end-learning-time"
              ></b-form-timepicker>
            </b-input-group-append>
          </b-input-group>
          <small v-if="isTimeInvalid" class="text-danger">
            กรุณาเลือกเวลาสิ้นสุด <br />หลังเวลาเริ่มต้น
          </small>
        </b-form-group>
      </b-col>
      <b-col cosl="3">
        <b-form-group label="จำนวนครั้งที่เรียน">
          <b-input-group append="ครั้ง">
            <b-form-input
              :value="formatNumberInput(form.classNumber)"
              @input="(val) => updateValue('classNumber', val)"
              @keydown="onlyNumberKey"
              placeholder="0"
            ></b-form-input>
          </b-input-group>
        </b-form-group>
      </b-col>
      <b-col cosl="3">
        <b-form-group label="นาทีที่เรียนต่อครั้ง">
          <b-input-group append="นาที">
            <b-form-input
              :value="formatNumberInput(form.classDuration)"
              @input="(val) => updateValue('classDuration', val)"
              @keydown="onlyNumberKey"
              placeholder="0"
            ></b-form-input>
          </b-input-group>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col cols="6">
        <b-form-group class="study-days">
          <template #label>
            วันเรียนในสัปดาห์ <span class="text-danger">*</span>
          </template>
          <b-form-tags
            id="study-days"
            v-model="form.classDays"
            no-outer-focus
            class="mb-2"
          >
            <template v-slot="{ tags, disabled, addTag, removeTag }">
              <ul
                v-if="tags?.length > 0"
                class="list-inline d-inline-block mb-2"
              >
                <li v-for="tag in tags" :key="tag" class="list-inline-item">
                  <b-form-tag @remove="removeTag(tag)" variant="info">
                    <p>{{ getDayName(tag) }}</p>
                  </b-form-tag>
                </li>
              </ul>

              <b-dropdown
                size="sm"
                variant="outline-secondary"
                block
                menu-class="w-100"
              >
                <template #button-content>
                  <b-icon icon="tag-fill"></b-icon> เลือกวันเรียน
                </template>

                <b-dropdown-item-button
                  v-for="option in availableDays"
                  :key="option.value"
                  @click="onOptionClick({ option: option.value, addTag })"
                >
                  {{ option.text }}
                </b-dropdown-item-button>
                <b-dropdown-text v-if="availableDays.length === 0">
                  ไม่มีวันให้เลือก
                </b-dropdown-text>
              </b-dropdown>
            </template>
          </b-form-tags>
        </b-form-group>
      </b-col>
      <b-col cols="6">
        <b-form-group label="ช่วงเทอมที่เปิด">
          <b-form-select
            v-model="form.termPeriod"
            :options="academicTerms"
          ></b-form-select>
        </b-form-group>
      </b-col>
      <b-col cols="6" class="mb-4">
        <label>วัน/เวลา ที่เปิดทำรายการสั่งซื้อ</label>
        <DateTimePicker
          v-model="form.openOrderDateTime"
          :options="openOrderOptions"
          fieldName="OPEN_ORDER"
          :courseInfo="courseOnsiteInfo"
        />
      </b-col>
      <b-col cols="6" class="mb-4">
        <label>วัน/เวลา ที่ปิดทำรายการสั่งซื้อ</label>
        <DateTimePicker
          v-model="form.closeOrderDateTime"
          :options="closeOrderOptions"
          fieldName="CLOSE_ORDER"
          :courseInfo="courseOnsiteInfo"
        />
      </b-col>
      <b-col cols="6" class="mb-4">
        <label>วัน/เวลา ที่เปิดรับชำระเงิน</label>
        <DateTimePicker
          v-model="form.openPaymentDateTime"
          :options="openPaymentOptions"
          fieldName="OPEN_PAYMENT"
          :courseInfo="courseOnsiteInfo"
        />
      </b-col>
      <b-col cols="6" class="mb-4">
        <label>วัน/เวลา ที่สิ้นสุดรับชำระเงิน</label>
        <DateTimePicker
          v-model="form.closePaymentDateTime"
          :options="closePaymentOptions"
          fieldName="CLOSE_PAYMENT"
          :courseInfo="courseOnsiteInfo"
        />
      </b-col>
      <b-col cols="6">
        <div class="day-off-section">
          <b-form-group label="วันหยุด">
            <b-form-datepicker
              v-model="dayOffSelected"
              id="days-off"
              placeholder="เลือกวันหยุด"
              locale="th"
              :min="dateFormat"
              @input="getDayOff"
              :date-disabled-fn="disableSelectedDates"
            ></b-form-datepicker>
          </b-form-group>
          <div v-if="daysOff.length > 0" class="day-off-selected-section">
            <div
              v-for="(date, index) in daysOff"
              :key="index"
              class="day-off-selected-list"
            >
              <b-input-group class="mr-2">
                <b-form-input
                  :id="`day-off-${index}`"
                  :value="formatDate(daysOff[index])"
                  type="text"
                  placeholder="YYYY-MM-DD"
                  autocomplete="off"
                ></b-form-input>
                <b-input-group-append>
                  <b-form-datepicker
                    v-model="daysOff[index]"
                    button-only
                    right
                    locale="th"
                    :aria-controls="`day-off-${index}`"
                    :date-disabled-fn="disableSelectedDates"
                  ></b-form-datepicker>
                </b-input-group-append>
              </b-input-group>

              <b-button variant="outline-danger" @click="removeDayOff(index)">
                <b-icon icon="trash"></b-icon>
              </b-button>
            </div>
          </div>
          <span v-else class="day-off-text">ไม่มีวันหยุด</span>
        </div>
      </b-col>
    </b-row>
    <b-row class="mt-4">
      <b-col cols="12" class="text-right">
        <b-button
          variant="primary"
          @click="submit()"
          :disabled="
            isTimeInvalid || isRequireFieldsEmpty || !isValidPromotionPrice
          "
        >
          บันทึก
        </b-button>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import Numeral from "numeral";
import DateTimePicker from "../../../shared/DateTimePicker.vue";
import moment from "moment";

export default {
  components: { DateTimePicker },
  computed: {
    ...mapGetters("CourseManagement", ["courseInfo", "courseOnsiteInfo"]),
    isValidPromotionPrice() {
      const { price, promotionPrice } = this.form;

      if (!price || !promotionPrice) return true;

      const promo = Numeral(promotionPrice).value() || 0;
      const original = Numeral(price).value() || 0;

      return promo > 0 && promo < original;
    },
    promotionState() {
      const { price, promotionPrice } = this.form;

      if (!price || !promotionPrice) return null;

      return this.isValidPromotionPrice;
    },
    availableDays() {
      return this.days.filter(
        (day) => !this.form?.classDays?.includes(day.value),
      );
    },
    openOrderOptions() {
      return {
        ...this.baseOptions,
        minDate: moment(),
        maxDate: this.form.closeOrderDateTime
          ? this.form.closeOrderDateTime
          : false,
      };
    },
    closeOrderOptions() {
      return {
        ...this.baseOptions,
        minDate: this.form.openOrderDateTime
          ? this.form.openOrderDateTime
          : moment(),
      };
    },
    openPaymentOptions() {
      return {
        ...this.baseOptions,
        minDate: moment(),
        maxDate: this.form.closePaymentDateTime
          ? this.form.closePaymentDateTime
          : false,
      };
    },
    closePaymentOptions() {
      return {
        ...this.baseOptions,
        minDate: this.form.openPaymentDateTime
          ? this.form.openPaymentDateTime
          : moment(),
      };
    },
    isTimeInvalid() {
      if (!this.classFinishTime || !this.classStartTime) return false;

      const start = moment(this.classStartTime, "HH:mm");
      const end = moment(this.classFinishTime, "HH:mm");

      return !end.isAfter(start); // Returns TRUE if there IS an error
    },
    isRequireFieldsEmpty() {
      const fields = [
        this.form.price,
        this.form.classDays,
        this.startLearningDate,
        this.finishLearningDate,
        this.classStartTime,
        this.classFinishTime,
      ];

      return fields.some((field) => {
        // Check if null or undefined
        if (field == null) return true;

        // Check if it's a string and empty (after removing spaces)
        if (typeof field === "string" && field.trim() === "") return true;

        if (Array.isArray(field) && field.length === 0) return true;

        return false;
      });
    },
    formattedStartLearningDate() {
      return this.formatDate(this.startLearningDate);
    },
    formattedFinishLearningDate() {
      return this.formatDate(this.finishLearningDate);
    },
  },
  data() {
    return {
      form: {
        price: null,
        promotionPrice: null,
        classNumber: null,
        classDuration: null,
        classDays: [],
        openOrderDateTime: "",
        closeOrderDateTime: "",
        openPaymentDateTime: "",
        closePaymentDateTime: "",
      },
      startLearningDate: "",
      finishLearningDate: "",
      classStartTime: "",
      classFinishTime: "",
      days: [
        { value: "Sunday", text: "อาทิตย์" },
        { value: "Monday", text: "จันทร์" },
        { value: "Tuesday", text: "อังคาร" },
        { value: "Wednesday", text: "พุธ" },
        { value: "Thursday", text: "พฤหัสบดี" },
        { value: "Friday", text: "ศุกร์" },
        { value: "Saturday", text: "เสาร์" },
      ],
      academicTerms: [
        { value: "OPEN", text: "เปิดเทอม" },
        { value: "CLOSE", text: "ปิดเทอม" },
      ],
      dayOffSelected: "",
      daysOff: [],
      baseOptions: {
        singleDatePicker: true,
        showDropdowns: true, // Optional: adds month/year dropdowns for easier nav
        drops: "down",
        timePicker: true, // Enable time
        timePicker24Hour: true, // Use 24h format
        autoUpdateInput: false,
        locale: {
          format: "YYYY-MM-DD HH:mm:ss", // Format for a single date
          daysOfWeek: ["อา", "จ", "อ", "พ", "พฤ", "ศ", "ส"],
          monthNames: [
            "มกราคม",
            "กุมภาพันธ์",
            "มีนาคม",
            "เมษายน",
            "พฤษภาคม",
            "มิถุนายน",
            "กรกฎาคม",
            "สิงหาคม",
            "กันยายน",
            "ตุลาคม",
            "พฤศจิกายน",
            "ธันวาคม",
          ],
          cancelLabel: "Clear",
        },
      },
      dateFormat: moment().format("YYYY-MM-DD"),
    };
  },
  methods: {
    ...mapActions("CourseManagement", ["updateCourseOnsiteInfo"]),
    async submit() {
      const cleanNumber = (val, fallback = null) => {
        if (val === null || val === undefined || val === "") return fallback;
        const sanitized = String(val).replace(/,/g, "");
        const num = Number(sanitized);
        return isNaN(num) ? fallback : num;
      };
      const formatDate = (val) => (val ? moment(val).format("YYYY-MM-DD") : "");
      const formatDateTime = (val) =>
        val ? moment(val).format("YYYY-MM-DD HH:mm:ss") : "";

      const numberFields = [
        "price",
        "promotionPrice",
        "classNumber",
        "classDuration",
      ];

      let params = numberFields.reduce((acc, key) => {
        acc[key] = cleanNumber(this.form[key], null);
        return acc;
      }, {});

      params.courseCode = this.form.courseCode ?? null;
      params.termPeriod = this.form.termPeriod ?? null;
      params.classDays = this.form.classDays ?? null;
      params.classStartTime = this.classStartTime;
      params.classFinishTime = this.classFinishTime;
      params.startLearningDate = formatDate(this.startLearningDate);
      params.finishLearningDate = formatDate(this.finishLearningDate);
      params.openOrderDateTime = formatDateTime(this.form.openOrderDateTime);
      params.closeOrderDateTime = formatDateTime(this.form.closeOrderDateTime);
      params.openPaymentDateTime = formatDateTime(
        this.form.openPaymentDateTime,
      );
      params.closePaymentDateTime = formatDateTime(
        this.form.closePaymentDateTime,
      );
      params.holidays = this.daysOff;

      await this.updateCourseOnsiteInfo({
        courseId: this.$route.params.courseId,
        attribute: params,
      });
      this.$emit("toggle-modal", "ONSITE");
    },
    formatNumberInput(value) {
      if (!value && value !== 0) return "";
      return Numeral(value).format("0,0");
    },
    updateValue(field, val) {
      this.form[field] = val;
    },
    onlyNumberKey(e) {
      const controlKeys = [
        "Backspace",
        "Delete",
        "Tab",
        "Escape",
        "Enter",
        "ArrowLeft",
        "ArrowRight",
      ];
      if (controlKeys.includes(e.key)) {
        return;
      }

      if (!/^[0-9]$/.test(e.key)) {
        e.preventDefault();
      }
    },
    getDayName(val) {
      const day = this.days.find((d) => d.value === val);
      return day ? day.text : val;
    },
    async onOptionClick({ option, addTag }) {
      await addTag(option);
    },
    disableSelectedDates(ymd) {
      return this.daysOff.includes(ymd);
    },
    getDayOff(date) {
      if (date && !this.daysOff.includes(date)) {
        this.daysOff.push(date);
        this.dayOffSelected = "";
      }
    },
    removeDayOff(index) {
      this.daysOff.splice(index, 1);
      if (this.daysOff.length === 0) {
        this.dayOffSelected = "";
      }
    },
    formatDate(value) {
      if (!value) {
        return "";
      }
      const yearMonthDate = value.split(" ")[0].split("-");
      const thaiYear = parseInt(yearMonthDate[0], 10) + 543;
      return moment(value).format("DD-MM-") + thaiYear;
    },
  },
  watch: {
    courseOnsiteInfo(newValue) {
      if (!newValue) return;

      const fields = [
        "courseCode",
        "price",
        "promotionPrice",
        "startLearningDate",
        "finishLearningDate",
        "classStartTime",
        "classFinishTime",
        "classNumber",
        "classDuration",
        "classDays",
        "termPeriod",
        "holidays",
      ];

      const dateFields = [
        "openOrderDateTime",
        "closeOrderDateTime",
        "openPaymentDateTime",
        "closePaymentDateTime",
      ];

      fields.forEach((key) => {
        this.form[key] = newValue[key] ?? null;
      });

      dateFields.forEach((key) => {
        this.form[key] = newValue[key] ?? "";
      });

      this.daysOff = newValue.holidays ?? [];

      this.classStartTime = newValue.classStartTime ?? "";
      this.classFinishTime = newValue.classFinishTime ?? "";

      this.startLearningDate = newValue.startLearningDate ?? "";
      this.finishLearningDate = newValue.finishLearningDate ?? "";
    },
  },
};
</script>

<style lang="scss" scoped>
.day-off-section {
  display: flex;
  flex-direction: column;

  .day-off-title {
    label {
      margin-bottom: 0;
    }
  }

  .day-off-text {
    background-color: #f8f9fa;
    color: #6c7585;
    padding: 16px;
    height: 150px;
  }

  .day-off-selected-section {
    background-color: #f8f9fa;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-right: 10px;
  }

  .datepicker-scroll-container::-webkit-scrollbar {
    width: 6px;
  }
  .datepicker-scroll-container::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 10px;
  }

  .day-off-selected-list {
    display: flex;
    gap: 5px;
  }
}
</style>

<style lang="scss">
.study-days {
  .b-form-tag-content {
    margin-bottom: 0 !important;
  }

  .close {
    font-weight: 300;
    font-size: 24px !important;
    align-self: center;
    text-shadow: none;
    line-height: 0.5 !important;
    opacity: 1;
  }
}
</style>
